// Replace the entire file with this updated version:

import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate, useLocation } from "react-router-dom";
import axios from 'axios';
import './TicketDetailsPage.css';
import Loader from "../../components/helpers/Loader";
import TicketConversation from "./TicketConversation";

const TicketDetailsPage = () => {
  const API_BASE_URL = process.env.REACT_APP_SERVER_URL || '';
  const { ticketId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();

  const [ticket, setTicket] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [updatingStatus, setUpdatingStatus] = useState(false);
  const [updateMessage, setUpdateMessage] = useState(null);
  const [updateMessageType, setUpdateMessageType] = useState(null);
  const [showConversation, setShowConversation] = useState(false);

  // Get token safely
  const getToken = useCallback(() => {
    try {
      return typeof Storage !== 'undefined' ? localStorage.getItem('token') : null;
    } catch (error) {
      console.warn('localStorage not available:', error);
      return null;
    }
  }, []);

  // Navigate back to tickets list
  const handleBackToTickets = () => {
    navigate('/settings/admin-panel/ticket_management');
  };

  // Fetch ticket from API
  const fetchTicketFromAPI = useCallback(async () => {
    if (!ticketId) return;

    // const token = getToken();
    // if (!token) {
    //   setError('Authentication token not found');
    //   setLoading(false);
    //   return;
    // }

    try {
      setLoading(true);
      const response = await axios.get(
        `${API_BASE_URL}/api/support/all-tickets`,
        // {
        //   headers: {
        //     Authorization: `Bearer ${token}`,
        //   },
        // }
      );

      const ticketData = response.data.find(t => (t._id || t.id) === ticketId);
      if (ticketData) {
        setTicket(ticketData);
        setError(null);
      } else {
        setError('Ticket not found');
      }
    } catch (err) {
      console.error('Error fetching ticket details:', err);
      setError(err.response?.data?.message || 'Failed to fetch ticket details');
    } finally {
      setLoading(false);
    }
  }, [ticketId, API_BASE_URL]);

  // Fetch ticket details
  useEffect(() => {
    const initializeTicketData = () => {
      const passedTicketData = location.state?.ticketData;

      if (passedTicketData && (passedTicketData._id || passedTicketData.id) === ticketId) {
        setTicket(passedTicketData);
        setLoading(false);
        setError(null);
        return;
      }

      fetchTicketFromAPI();
    };

    if (ticketId) {
      initializeTicketData();
    } else {
      setError('Ticket ID is required');
      setLoading(false);
    }
  }, [ticketId, fetchTicketFromAPI, location.state]);

  // Clear messages after timeout
  useEffect(() => {
    if (updateMessage) {
      const timer = setTimeout(() => {
        setUpdateMessage(null);
        setUpdateMessageType(null);
      }, updateMessageType === 'success' ? 3000 : 5000);

      return () => clearTimeout(timer);
    }
  }, [updateMessage, updateMessageType]);

  // Update ticket status
  const updateTicketStatus = async (newStatus) => {
    // const token = getToken();
    // if (!token) {
    //   setUpdateMessage('Authentication token not found');
    //   setUpdateMessageType('error');
    //   return;
    // }

    // Clear any existing messages
    setUpdateMessage(null);
    setUpdateMessageType(null);

    try {
      setUpdatingStatus(true);

      const response = await axios.patch(
        `${API_BASE_URL}/api/support/ticket/${ticketId}/status`,
        { status: newStatus },
        // {
        //   headers: {
        //     Authorization: `Bearer ${token}`,
        //     'Content-Type': 'application/json',
        //   },
        // }
      );

      if (response.data.success) {
        // Update local state with the response data
        setTicket(response.data.data);
        setUpdateMessage(`Status successfully updated to ${newStatus}`);
        setUpdateMessageType('success');

        // Refresh data from server to ensure consistency
        setTimeout(() => {
          fetchTicketFromAPI();
        }, 1000);
      } else {
        throw new Error(response.data.message || 'Update failed');
      }

    } catch (err) {
      console.error('Error updating ticket status:', err);
      const errorMessage = err.response?.data?.message || err.message || 'Failed to update ticket status';
      setUpdateMessage(errorMessage);
      setUpdateMessageType('error');
    } finally {
      setUpdatingStatus(false);
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleString("en-US", {
      year: "numeric",
      month: "long",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const getStatusColor = (status) => {
    switch (status?.toLowerCase()) {
      case 'open':
        return '#007bff';
      case 'in progress':
        return '#f57c00';
      case 'resolved':
        return '#7b1fa2';
      case 'closed':
        return '#28a745';
      default:
        return '#6c757d';
    }
  };

  if (loading) {
    return (
      <div className="ticket-details-loading">
        <Loader size={50} color="#007bff" secondaryColor="#f3f3f3" />
        <p>Loading ticket details...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="ticket-details-error">
        <h2>Error</h2>
        <p>{error}</p>
        <button onClick={handleBackToTickets} className="ticketpage-back-button">
          Back to Tickets
        </button>
      </div>
    );
  }

  if (!ticket) {
    return (
      <div className="ticket-details-error">
        <h2>Ticket Not Found</h2>
        <p>The requested ticket could not be found.</p>
        <button onClick={handleBackToTickets} className="ticketpage-back-button">
          Back to Tickets
        </button>
      </div>
    );
  }

  return (
    <div className="ticket-details-page">
      <div className="ticket-details-header">
        <button onClick={handleBackToTickets} className="ticketpage-back-button">
          ← Back to Tickets
        </button>
        <h1>Ticket Details</h1>
      </div>

      <div className="ticket-details-container">
        <div className="ticket-details-main">
          <div className="ticket-info-card">
            <div className="card-header">
              <h2>Ticket Information</h2>
              <span
                className="status-badge-large"
                style={{ backgroundColor: getStatusColor(ticket.status) }}
              >
                {ticket.status}
              </span>
            </div>

            <div className="card-content">
              <div className="info-row">
                <div className="info-item">
                  <label>Ticket ID:</label>
                  <span className="ticket-id">
                    {(ticket._id || ticket.id) ?
                      (ticket._id ? ticket._id.substring(ticket._id.length - 8) : ticket.id.toString())
                      : 'N/A'}
                  </span>
                </div>
                <div className="info-item">
                  <label>Created Date:</label>
                  <span>{formatDate(ticket.createdAt)}</span>
                </div>
              </div>

              <div className="info-row">
                <div className="info-item">
                  <label>Last Updated:</label>
                  <span>{formatDate(ticket.updatedAt || ticket.createdAt)}</span>
                </div>
                {/* <div className="info-item">
                  <label>Priority:</label>
                  <span className="priority-badge">
                    {ticket.priority || 'Medium'}
                  </span>
                </div> */}
              </div>
            </div>
          </div>

          <div className="ticket-user-info-card">
            <div className="card-header">
              <h2>User Information</h2>
            </div>

            <div className="card-content">
              <div className="info-row">
                <div className="info-item">
                  <label>Name:</label>
                  <span className="user-name">{ticket.name}</span>
                </div>
                <div className="info-item">
                  <label>Email:</label>
                  <span className="user-email">{ticket.email}</span>
                </div>
              </div>
            </div>
          </div>

          <div className="description-card">
            <div className="card-header">
              <h2>Issue Description</h2>
            </div>

            <div className="card-content">
              <div className="description-content">
                {ticket.description}
              </div>
            </div>
          </div>
        </div>

        <div className="ticket-details-sidebar">
          <div className="actions-card">
            <div className="card-header">
              <h3>Actions</h3>
            </div>

            <div className="card-content">
              {/* Add conversation button */}
              <div className="conversation-section">
                <button
                  onClick={() => setShowConversation(true)}
                  className="conversation-btn"
                >
                  💬 Start Conversation
                </button>
              </div>

              <div className="status-update-section">
                <label>Update Status:</label>
                <div className="status-buttons">
                  <button
                    onClick={() => updateTicketStatus('Open')}
                    disabled={updatingStatus || ticket.status === 'Open'}
                    className={`status-btn open ${ticket.status === 'Open' ? 'active' : ''}`}
                  >
                    Open
                  </button>
                  <button
                    onClick={() => updateTicketStatus('In Progress')}
                    disabled={updatingStatus || ticket.status === 'In Progress'}
                    className={`status-btn in-progress ${ticket.status === 'In Progress' ? 'active' : ''}`}
                  >
                    In Progress
                  </button>
                  <button
                    onClick={() => updateTicketStatus('Resolved')}
                    disabled={updatingStatus || ticket.status === 'Resolved'}
                    className={`status-btn resolved ${ticket.status === 'Resolved' ? 'active' : ''}`}
                  >
                    Resolved
                  </button>
                  <button
                    onClick={() => updateTicketStatus('Closed')}
                    disabled={updatingStatus || ticket.status === 'Closed'}
                    className={`status-btn closed ${ticket.status === 'Closed' ? 'active' : ''}`}
                  >
                    Closed
                  </button>
                </div>

                {updatingStatus && <p className="updating-text">Updating...</p>}

                {updateMessage && (
                  <div className={`update-message ${updateMessageType}`}>
                    {updateMessage}
                  </div>
                )}
              </div>
            </div>
          </div>

          <div className="metadata-card">
            <div className="card-header">
              <h3>Metadata</h3>
            </div>

            <div className="card-content">
              <div className="metadata-item">
                <label>Full Ticket ID:</label>
                <span className="metadata-value">{ticket._id || ticket.id}</span>
              </div>

              <div className="metadata-item">
                <label>Source:</label>
                <span className="metadata-value">Support Form</span>
              </div>

              <div className="metadata-item">
                <label>Category:</label>
                <span className="metadata-value">{ticket.category || 'General'}</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Conversation Modal */}
      {showConversation && (
        <TicketConversation
          ticketId={ticketId}
          ticketEmail={ticket.email}
          onClose={() => setShowConversation(false)}
        />
      )}
    </div>
  );
};


export default TicketDetailsPage;